import type { MergeTagCategory, MergeTagDefinition } from "./catalog";

/**
 * Shape of one customer-metadata merge tag from GET .../installed
 * (`customerMetadataTags`). Kept local to the merge-tags service so the
 * agnostic catalog does not import connect-ui connector types.
 *
 * Hierarchy from connectors:
 *   connector → (optional) productLine → tags
 * Identifiers always include the connector id:
 *   customer.metadata.{connectorId}__{key}
 */
export interface CustomerMetadataTagInput {
  identifier: string;
  label: string;
  preview: string;
  categoryLabel: string;
  tagType?: "text" | "url";
  connectorId?: string;
  productLine?: string;
}

function categoryIdFor(
  tag: CustomerMetadataTagInput,
  categoryLabel: string,
): string {
  if (tag.connectorId) {
    const line = tag.productLine?.trim() || "default";
    return `connector-metadata:${tag.connectorId}:${line}`;
  }
  return `connector-metadata:${categoryLabel.toLowerCase().replace(/\s+/g, "-")}`;
}

/**
 * Groups installed-connector `customerMetadataTags` into merge-tag categories.
 * One category per connector product line (e.g. "Yotpo Loyalty"), or one
 * category per connector when tags are connector-level (no product line).
 */
export function customerMetadataTagsToCategories(
  tags: readonly CustomerMetadataTagInput[],
): MergeTagCategory[] {
  if (tags.length === 0) return [];

  const categories: MergeTagCategory[] = [];
  const indexById = new Map<string, number>();

  for (const tag of tags) {
    const categoryLabel =
      tag.categoryLabel.trim() || tag.connectorId || "Connector";
    const id = categoryIdFor(tag, categoryLabel);
    let idx = indexById.get(id);
    if (idx === undefined) {
      idx = categories.length;
      indexById.set(id, idx);
      categories.push({
        id,
        label: categoryLabel,
        tags: [],
      });
    }

    const def: MergeTagDefinition = {
      identifier: tag.identifier,
      label: tag.label,
      preview: tag.preview,
      categoryId: categories[idx].id,
      availability: "always",
      // No pre-filled default: a connector's preview is a picker sample, not a
      // render fallback — auto-defaulting would leak e.g. "Gold" as every
      // tier-less recipient's real tier. Merchant opts in via the chip popover.
      filters: ["default"],
      tagType: tag.tagType === "url" ? "url" : "text",
    };
    categories[idx].tags.push(def);
  }

  return categories;
}

/**
 * Flattens `customerMetadataTags` from every installed connector.
 */
export function collectCustomerMetadataTags(
  connectors: readonly {
    customerMetadataTags?: readonly CustomerMetadataTagInput[];
  }[],
): CustomerMetadataTagInput[] {
  const out: CustomerMetadataTagInput[] = [];
  for (const connector of connectors) {
    if (!connector.customerMetadataTags?.length) continue;
    out.push(...connector.customerMetadataTags);
  }
  return out;
}

/**
 * Returns merge tags for one selected connector event. Event payload tags are
 * intentionally not combined across events because their payload shapes differ.
 */
export function collectEventMergeTags(
  events:
    | readonly {
        id: string;
        mergeTags?: readonly CustomerMetadataTagInput[];
      }[]
    | undefined,
  selectedEventId: string | undefined,
): CustomerMetadataTagInput[] {
  if (!selectedEventId) return [];
  return [
    ...(events?.find((event) => event.id === selectedEventId)?.mergeTags ?? []),
  ];
}
